import React from 'react';
import { Phone, CheckCircle, Star, Shield, Zap, MapPin } from 'lucide-react';
import { Header, Footer, useQuoteModal, usePageMeta } from '../components/SharedComponents';
import { getServicePage } from '../servicePages';
import reviewData from '../reviews.json';

const COUNTIES_LINE = 'Anne Arundel, Howard, Baltimore & Carroll counties';

// One component renders all four service landing pages; the slug prop picks
// the content from servicePages.js. The emergency page swaps the calm green
// hero for a phone-first amber one — most storm traffic is on a phone at the
// curb, so the call button is the first thing on screen.
const ServicePage = ({ slug }) => {
  const page = getServicePage(slug);
  const { QuoteModal, openQuoteModal } = useQuoteModal();

  usePageMeta(page.metaTitle, page.metaDescription);

  const related = page.related.map(getServicePage).filter(Boolean);
  const review = reviewData.reviews.find(r => r.text.length >= 60 && !r.text.trimEnd().endsWith('...'));

  return (
    <div className="min-h-screen bg-white">
      <Header currentPage="services" />

      {/* Hero */}
      <section
        className={`pt-24 pb-12 ${
          page.emergency
            ? 'bg-gradient-to-br from-amber-50 to-orange-50'
            : 'bg-gradient-to-br from-green-50 to-blue-50'
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div
            className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6 ${
              page.emergency ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'
            }`}
          >
            {page.emergency ? <Zap className="w-4 h-4 mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            {page.emergency ? 'Crews on call day and night' : 'Licensed & Insured in Maryland'}
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {page.heroLine}
          </h1>

          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            {page.intro}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+14432080747"
              className={`text-white rounded-lg font-semibold flex items-center justify-center space-x-2 shadow-lg transition-colors duration-200 ${
                page.emergency
                  ? 'bg-amber-600 hover:bg-amber-700 px-10 py-5 text-xl'
                  : 'bg-green-600 hover:bg-green-700 px-8 py-4 text-lg'
              }`}
            >
              <Phone className={page.emergency ? 'w-6 h-6' : 'w-5 h-5'} />
              <span>Call (443) 208-0747</span>
            </a>
            <button
              onClick={() => openQuoteModal(page.quoteService)}
              className={`border-2 rounded-lg font-semibold text-lg transition-colors duration-200 px-8 py-4 ${
                page.emergency
                  ? 'border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white'
                  : 'border-green-600 text-green-600 hover:bg-green-600 hover:text-white'
              }`}
            >
              Get a Free Quote
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-600 mt-8">
            <div className="flex items-center space-x-1">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <span className="font-medium">{reviewData.rating.toFixed(1)}/5 from {reviewData.count} reviews</span>
            </div>
            <div className="flex items-center space-x-1">
              <Shield className="w-4 h-4 text-green-600" />
              <span>Fully Insured</span>
            </div>
            <div className="flex items-center space-x-1">
              <MapPin className="w-4 h-4 text-green-600" />
              <span>Serving {COUNTIES_LINE}</span>
            </div>
          </div>
        </div>
      </section>

      {/* How the work is done */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-3 text-center">
            How We Handle {page.name}
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
            No mystery, no upsell — here's what the job actually involves.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {page.notes.map((note, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-green-600 text-white flex items-center justify-center font-display text-lg mb-4">
                  {i + 1}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{note.title}</h3>
                <p className="text-gray-600 leading-relaxed">{note.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Review */}
      {review && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex justify-center text-yellow-400 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-current" />
              ))}
            </div>
            <p className="text-xl text-gray-700 italic leading-relaxed mb-4">"{review.text}"</p>
            <p className="text-gray-500 font-medium">— {review.name}, Google Review</p>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">
            {page.name} Questions, Answered Straight
          </h2>
          <div className="space-y-8">
            {page.faqs.map((faq, i) => (
              <div key={i}>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Other Work We Do</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {related.map(service => (
              <a
                key={service.slug}
                href={`/${service.slug}`}
                className="bg-green-100 text-green-700 hover:bg-green-200 px-4 py-2 rounded-full font-medium transition-colors"
              >
                {service.name}
              </a>
            ))}
            <a
              href="/services"
              className="bg-white border border-gray-200 text-gray-600 hover:border-green-600 hover:text-green-700 px-4 py-2 rounded-full font-medium transition-colors"
            >
              All services
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 text-white ${page.emergency ? 'bg-amber-600' : 'bg-green-600'}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            {page.emergency ? 'Storm Damage Right Now?' : `Need ${page.name}?`}
          </h2>
          <p className={`text-xl mb-8 ${page.emergency ? 'text-amber-100' : 'text-green-100'}`}>
            {page.emergency
              ? "Call — we'll tell you what to do next, even before we roll."
              : 'Free on-site assessment, honest answer, no pressure.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+14432080747"
              className={`bg-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors ${
                page.emergency ? 'text-amber-700 hover:bg-amber-50' : 'text-green-700 hover:bg-green-50'
              }`}
            >
              Call (443) 208-0747
            </a>
            <button
              onClick={() => openQuoteModal(page.quoteService)}
              className={`border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors hover:bg-white ${
                page.emergency ? 'hover:text-amber-700' : 'hover:text-green-700'
              }`}
            >
              Get a Free Quote
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <QuoteModal />
    </div>
  );
};

export default ServicePage;
